import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { languages, LanguageCode, isRTLLanguage } from '../../i18n/languages';
import { cn } from '../../lib/utils';

interface LanguageSelectorProps {
  variant?: 'default' | 'compact' | 'full';
  className?: string;
  showNativeName?: boolean;
  showFlag?: boolean;
}

export function LanguageSelector({
  variant = 'default',
  className,
  showNativeName = true,
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages[i18n.language as LanguageCode] || languages.en;
  const currentCode = (i18n.language as LanguageCode) || 'en';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode: LanguageCode) => {
    await i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
  };

  // Group languages by RTL/LTR for better organization
  const ltrLanguages = Object.entries(languages).filter(([_, lang]) => lang.dir === 'ltr');
  const rtlLanguages = Object.entries(languages).filter(([_, lang]) => lang.dir === 'rtl');

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 rounded-lg transition-colors',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-blue-500',
          variant === 'compact' ? 'p-2' : 'px-3 py-2',
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('language.selectLanguage')}
      >
        <Globe className="w-4 h-4 text-gray-500" />
        {variant !== 'compact' && (
          <>
            <span className="text-sm font-medium">
              {showNativeName ? currentLang.nativeName : currentLang.name}
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-64 max-h-96 overflow-y-auto',
            'bg-white dark:bg-gray-900 rounded-lg shadow-lg',
            'border border-gray-200 dark:border-gray-700',
            // Position based on RTL
            isRTLLanguage(i18n.language) ? 'left-0' : 'right-0',
          )}
          role="listbox"
          aria-label={t('language.selectLanguage')}
        >
          {/* LTR Languages */}
          <div className="p-2">
            <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">
              {t('builder:formLanguages.leftToRight', 'Left-to-Right')}
            </div>
            {ltrLanguages.map(([code, lang]) => (
              <LanguageOption
                key={code}
                code={code as LanguageCode}
                lang={lang}
                isSelected={currentCode === code}
                onClick={() => handleLanguageChange(code as LanguageCode)}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700" />

          {/* RTL Languages */}
          <div className="p-2">
            <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
              {t('builder:formLanguages.rightToLeft', 'Right-to-Left')}
              <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">
                RTL
              </span>
            </div>
            {rtlLanguages.map(([code, lang]) => (
              <LanguageOption
                key={code}
                code={code as LanguageCode}
                lang={lang}
                isSelected={currentCode === code}
                onClick={() => handleLanguageChange(code as LanguageCode)}
                isRTL
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface LanguageOptionProps {
  code: LanguageCode;
  lang: (typeof languages)[LanguageCode];
  isSelected: boolean;
  onClick: () => void;
  isRTL?: boolean;
}

function LanguageOption({ lang, isSelected, onClick, isRTL }: LanguageOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        isSelected && 'bg-blue-50 dark:bg-blue-900/20',
      )}
      role="option"
      aria-selected={isSelected}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <span className="flex-1 text-start">
        <span className="font-medium">{lang.nativeName}</span>
        <span className="text-gray-500 text-xs block">{lang.name}</span>
      </span>
      {isSelected && <Check className="w-4 h-4 text-blue-500" />}
    </button>
  );
}

export default LanguageSelector;
